import { DataLoader } from 'src/app/services/data-loader/data-loader';
import { DataLoaderResult } from 'src/app/services/data-loader/data-loader-result';
import { standardRun } from 'src/app/services/data-loader/utils/standard-run';
import { Factory } from 'src/app/utils/Factory';
import {
    ICreatedInstanceId,
    IGcpProjectId,
    IQueryCacheService,
} from 'src/app/utils/IFactory';
import { generateTableFullPath } from 'src/app/utils/datasource-sql-map/datasource';
import { bq2echartsformat } from 'src/app/utils/echarts/echarts';
import { XYChartAxis } from 'src/app/utils/echarts/xy-chart-axis';
import { BiVizDashboardCompanyPlayerKpisDailySqlMap } from '../sql-maps/biviz-dashboard-company-player-kpis-daily-sql-map';

export interface AdsMonthlyLast16MonthsDataLoaderFactoryOptions
    extends ICreatedInstanceId, IGcpProjectId, IQueryCacheService {}

export class AdsMonthlyLast16MonthsDataLoaderFactory extends Factory<
    DataLoader,
    AdsMonthlyLast16MonthsDataLoaderFactoryOptions
> {
    override FACTORY_CLASS: string = 'AdsMonthlyLast16MonthsDataLoaderFactory';
    /** This function returns DataLoader instance for analytics vs store reports differences */

    getInstance(options: AdsMonthlyLast16MonthsDataLoaderFactoryOptions) {
        // let options = verifyRequiredOptions(rawOptions);
        // if (!verifyRequiredOptions(options)) {
        //     throw Error(
        //         '[getRetentionByFirstOpenDateDataLoaderFactory] Provided options are of type SalesDatetimeDataLoaderOptions'
        //     );
        // }
        return new DataLoader(options.createdInstanceId, async function (
            queryContext,
            cancellationToken
        ) {
            const process = async () => {
                this.emitter.emit(
                    DataLoaderResult.getLoading(this.latestData, queryContext)
                );

                const datasourceSqlMap =
                    BiVizDashboardCompanyPlayerKpisDailySqlMap.sqlMap;

                // filter out from context ETL_PAYER
                const datasourceColumns = queryContext.datasourceColumns;
                // const datasourceColumns = queryContext.datasourceColumns.filter(
                //     (dsCol) => dsCol.id !== 'ETL_PAYER'
                // );

                // identify columns not supported in datasource
                const dsColumns = DataLoader.separatePresentAndMissingDims(
                    datasourceColumns,
                    datasourceSqlMap.columns
                );

                const { pendingDataLoaderResultStatus, pendingStatusMessage } =
                    DataLoader.getMissingColumnsStatus(dsColumns.missing);

                const { splitSelect, splitGroupBy, where } =
                    BiVizDashboardCompanyPlayerKpisDailySqlMap.compileSQLStatements(
                        dsColumns.present,
                        queryContext.settings,
                        options,
                        {
                            where: [`date >= date_dim.months_ago_15`],
                        }
                    );

                const tableFullPath = generateTableFullPath(
                    options.gcpProjectId,
                    datasourceSqlMap.dataset,
                    datasourceSqlMap.tableId
                );

                const query = `
-- DataLoaderId: ${this.instanceId}

WITH date_dim AS (
    SELECT *
        , DATE_SUB(current_m, INTERVAL 15 MONTH) AS months_ago_15
    FROM 
    (
      SELECT DATE_TRUNC(MAX(date), MONTH) AS current_m FROM \`${tableFullPath}\`
    )
  )
  
  SELECT 
    ${splitSelect}DATE_TRUNC(date, MONTH) AS date
    , SUM(ads_kpis.impressions) AS impressions
    , SAFE_DIVIDE(SUM(ads_kpis.engaged_users), SUM(dau)) AS engagement_rate
    -- ads impressions per DEU
    , SAFE_DIVIDE(SUM(ads_kpis.impressions), SUM(ads_kpis.engaged_users)) AS impressions_per_deu
  FROM \`${tableFullPath}\`,
    date_dim
  ${where}
  GROUP BY ${splitGroupBy}date
  ORDER BY ${splitGroupBy}date
`;

                const queryResponse = await DataLoader.runQuery(
                    query,
                    options.queryCacheService
                );
                cancellationToken.throwIfCancellationRequested();
                var data = [...queryResponse.result];
                bq2echartsformat(data);

                XYChartAxis.fillMissingTimeGapRows(
                    data,
                    queryContext,
                    'date',
                    'month'
                );

                this.emitter.emit({
                    data,
                    queryContext,
                    status: pendingDataLoaderResultStatus,
                    statusMessage: pendingStatusMessage,
                    isLoading: false,
                    sqlAndJsProcessing: query,
                });
            };

            await standardRun(this, queryContext, process);
        });
    }
}
